/**
 * MicrophoneManagerSingleton - Simplified microphone access and MediaStream management
 * Provides direct access to getUserMedia stream without audio processing
 */
class MicrophoneManagerSingleton {
  constructor() {
    this.mediaStream = null
    this.audioContext = null
    this.analyser = null
    this.dataArray = null
    this.animationFrame = null
    this.audioLevelCallbacks = new Set()
    this.referenceCount = 0
    this.wakeLock = null
    this._wakeLockVisibilityHandler = null
  }

  /**
   * Request microphone permission from the user
   * @returns {Promise<boolean>} True if permission granted
   */
  async requestPermission() {
    try {
      // Check if we already have permission
      const permission = await navigator.permissions.query({
        name: 'microphone',
      })

      if (permission.state === 'granted') {
        return true
      }

      if (permission.state === 'denied') {
        throw new Error(
          'Microphone permission denied. Please enable microphone access in your browser settings.'
        )
      }

      // Request permission by attempting to access the microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: this.getAudioConstraints(),
      })

      // Stop the test stream immediately
      stream.getTracks().forEach((track) => track.stop())

      return true
    } catch (error) {
      this.handlePermissionError(error)
      return false
    }
  }

  /**
   * Acquire a reference to the MediaStream from the microphone
   * Increments reference count and initializes stream if needed
   * @returns {Promise<MediaStream>} The audio stream
   */
  async acquire() {
    this.referenceCount++
    console.log(
      `MicrophoneManager: Acquired reference (count: ${this.referenceCount})`
    )

    if (this.mediaStream) {
      return this.mediaStream
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: this.getAudioConstraints(),
      })

      // Initialize audio analysis
      await this.initializeAudioAnalysis()

      // Prevent screen from locking while the microphone is active
      await this.acquireWakeLock()

      return this.mediaStream
    } catch (error) {
      // If we failed to get the stream, decrement the count
      this.referenceCount--
      throw this.handleAudioContextError(error)
    }
  }

  /**
   * Release a reference to the MediaStream
   * Decrements reference count and cleans up if no more references
   */
  async release() {
    if (this.referenceCount <= 0) {
      console.warn(
        'MicrophoneManager: Attempted to release with no active references'
      )
      return
    }

    this.referenceCount--
    console.log(
      `MicrophoneManager: Released reference (count: ${this.referenceCount})`
    )

    if (this.referenceCount === 0) {
      console.log('MicrophoneManager: No more references, cleaning up')
      await this.stopStream()
    }
  }

  /**
   * Stop the media stream and cleanup resources
   */
  async stopStream() {
    // Stop audio analysis
    this.stopAudioAnalysis()

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop()
      })
      this.mediaStream = null
    }

    // Clean up audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close()
      this.audioContext = null
      this.analyser = null
      this.dataArray = null
    }

    await this.releaseWakeLock()
  }

  async acquireWakeLock() {
    if (!navigator.wakeLock) return
    try {
      this.wakeLock = await navigator.wakeLock.request('screen')
      // The browser silently releases the sentinel when the page becomes hidden.
      // Track that so the visibilitychange handler knows to re-acquire.
      this.wakeLock.addEventListener('release', () => { this.wakeLock = null })

      if (!this._wakeLockVisibilityHandler) {
        this._wakeLockVisibilityHandler = async () => {
          if (document.visibilityState === 'visible' && this.mediaStream && !this.wakeLock) {
            await this.acquireWakeLock()
          }
        }
        document.addEventListener('visibilitychange', this._wakeLockVisibilityHandler)
      }
    } catch {
      // Wake lock is non-critical — ignore errors (e.g. document not visible)
    }
  }

  async releaseWakeLock() {
    if (this._wakeLockVisibilityHandler) {
      document.removeEventListener('visibilitychange', this._wakeLockVisibilityHandler)
      this._wakeLockVisibilityHandler = null
    }
    if (!this.wakeLock) return
    const sentinel = this.wakeLock
    this.wakeLock = null
    try {
      await sentinel.release()
    } catch {
      // Ignore — already released or page hidden
    }
  }

  /**
   * Check if we have an active stream
   * @returns {boolean}
   */
  hasActiveStream() {
    return this.mediaStream !== null && this.mediaStream.active
  }

  /**
   * Get the current reference count
   * @returns {number} Current reference count
   */
  getReferenceCount() {
    return this.referenceCount
  }

  // Private methods

  getAudioConstraints() {
    return {
      sampleRate: 24000,
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    }
  }

  handlePermissionError(error) {
    if (error.name === 'NotAllowedError') {
      throw new Error(
        'Microphone access denied. Please allow microphone permissions and try again.'
      )
    } else if (error.name === 'NotFoundError') {
      throw new Error(
        'No microphone found. Please connect a microphone and try again.'
      )
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Your browser does not support microphone access.')
    } else {
      throw new Error(`Microphone error: ${error.message}`)
    }
  }

  handleAudioContextError(error) {
    if (error.name === 'NotAllowedError') {
      return new Error(
        'Microphone access denied. Please allow microphone permissions and try again.'
      )
    } else if (error.name === 'NotFoundError') {
      return new Error(
        'No microphone device found. Please connect a microphone and try again.'
      )
    } else if (error.name === 'NotSupportedError') {
      return new Error(
        'Your browser does not support the required audio features.'
      )
    } else if (error.name === 'OverconstrainedError') {
      return new Error(
        'The requested microphone settings are not supported by your device.'
      )
    } else {
      return new Error(`Audio initialization failed: ${error.message}`)
    }
  }

  // Audio analysis methods

  /**
   * Initialize audio analysis for volume visualization
   */
  async initializeAudioAnalysis() {
    if (!this.mediaStream) return

    try {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)()

      // Create analyser node
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 256
      this.analyser.smoothingTimeConstant = 0.8

      // Connect media stream to analyser
      const source = this.audioContext.createMediaStreamSource(this.mediaStream)
      source.connect(this.analyser)

      // Create data array for frequency data
      const bufferLength = this.analyser.frequencyBinCount
      this.dataArray = new Uint8Array(bufferLength)

      // Start analyzing audio levels
      this.startAudioAnalysis()
    } catch (error) {
      console.warn('Could not initialize audio analysis:', error)
    }
  }

  /**
   * Start the audio analysis loop
   */
  startAudioAnalysis() {
    if (!this.analyser || !this.dataArray) return

    const analyze = () => {
      if (!this.analyser || !this.dataArray) return

      this.analyser.getByteFrequencyData(this.dataArray)

      // Calculate average volume level
      const sum = this.dataArray.reduce((acc, value) => acc + value, 0)
      const average = sum / this.dataArray.length

      // Normalize to 0-1 range
      const normalizedLevel = Math.min(average / 128, 1)

      // Notify callbacks
      this.audioLevelCallbacks.forEach((callback) => {
        try {
          callback(normalizedLevel)
        } catch (error) {
          console.warn('Error in audio level callback:', error)
        }
      })

      this.animationFrame = requestAnimationFrame(analyze)
    }

    analyze()
  }

  /**
   * Stop the audio analysis loop
   */
  stopAudioAnalysis() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
    }
  }

  /**
   * Add callback for audio level updates
   * @param {Function} callback - Called with normalized audio level (0-1)
   */
  onAudioLevel(callback) {
    this.audioLevelCallbacks.add(callback)

    return () => {
      this.audioLevelCallbacks.delete(callback)
    }
  }

  /**
   * Remove all audio level callbacks
   */
  clearAudioLevelCallbacks() {
    this.audioLevelCallbacks.clear()
  }
}

// Export singleton instance
export const MicrophoneManager = new MicrophoneManagerSingleton()
